import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import { t, translate } from 'react-switch-lang';
import { Link } from 'react-router-dom';
import './contactusform.scss';
import './ContactUsPaySimply.scss';
import { useSelector } from 'react-redux';
import { SubmitContactUs } from '../../../helperFunctions/SubmitContactUs';
import { mailcheckValidate, validateEmail, validateName, validateBody } from './Validation';
import { setScreenReaderAlert } from '../../../helperFunctions/Accessibility';
import { performSearch } from '../../../components/algolia/AlgoliaUtils';
import RelevantFAQ from './RelevantFAQ';

function ContactUsPartners({ widgetMode }) {
  const { lang } = useSelector((state) => state);
  const emailRef = useRef();
  const bodyRef = useRef();
  const businessRef = useRef();
  const [error, setError] = useState('');
  const [showError, setShowError] = useState(false);
  const [disable, setDisable] = useState(false);
  const [product, setProduct] = useState('');

  const [validateEmailOnChange, setValidateEmailOnChange] = useState(false);
  const [validateDescriptionOnChange, setValidateDescriptionOnChange] = useState(false);
  const [validateBusinessOnChange, setValidateBusinessOnChange] = useState(false);
  const [validateProductOnChange, setValidateProductOnChange] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [suggestedArticleAlertRead, setSuggestedArticleAlertRead] = useState(false);
  const isMobile = (window.location.href.includes('mobileapp') ? '?mobileapp' : '');
  const [submitted, setSubmitted] = useState(false);

  const [showEmailConfirm, setShowEmailConfirm] = useState(false);
  const [runMailCheck, setRunMailCheck] = useState(true);
  const [invalidEmailSuggestion, setInvalidEmailSuggestion] = useState(null);

  const [formError, setFormError] = useState({
    customerEmail: null,
    businessName: null,
    description: null,
    product: null,
  });

  function sendAPI() {
    const data = {
      Brand: 'PSI Partners',
      BrandID: 'Partners',
      BusinessName: businessRef.current.value,
      Product: product,
      CustomerEmail: emailRef.current.value,
      Body: bodyRef.current.value,
      Language: lang,
    };

    const sending = document.getElementById('submit-button');
    sending.innerHTML = t('ContactForm_Submitting');
    setDisable(true);

    SubmitContactUs(data).then(
      () => {
        setShowError(false);
        document.getElementById('desktop-contact-us').style.visibility = 'hidden';
        document.getElementById('successMessage').style.visibility = 'visible';
        document.getElementById('successMessage').style.display = 'block';
        const focus = document.getElementById('successMessage');
        focus.scrollIntoView({ behavior: 'smooth', block: 'center' });
      },
      () => {
        setError(t('Error_Something_Went_Wrong'));
        setShowError(true);
        sending.innerHTML = t('ContactForm_Submit_button');
        setDisable(false);
        const focus = document.getElementById('error_message');
        focus.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    );
  }

  const handleTextareaChange = (e) => {
    setSearchTerm(e.target.value);
  };

  function validateFormFields(force) {
    const errors = {
      customerEmail: null,
      businessName: null,
      description: null,
      product: null,
    };
    if ((validateEmailOnChange || force) && emailRef.current) {
      const { value } = emailRef.current;
      errors.customerEmail = validateEmail(value);

      if (errors.customerEmail === null) {
        setInvalidEmailSuggestion(mailcheckValidate(value));
        if (runMailCheck && invalidEmailSuggestion !== null) {
          setShowEmailConfirm(true);
          errors.customerEmail = t('Contact_EmailSuggestion').replace('xEmail', invalidEmailSuggestion);
        } else {
          setShowEmailConfirm(false);
        }
      } else {
        setShowEmailConfirm(false);
      }
    }

    if ((validateBusinessOnChange || force) && businessRef.current) {
      const { value } = businessRef.current;
      errors.businessName = validateName(value);
    }

    if ((validateDescriptionOnChange || force) && bodyRef.current) {
      const { value } = bodyRef.current;
      errors.description = validateBody(value);
    }

    if (validateProductOnChange || force) {
      errors.product = ((product === '') ? t('Validation_Required') : null);
    }

    setFormError(errors);
    return errors;
  }

  useEffect(() => {
    if (!submitted) return;
    const hasErrors = Object.values(formError).some((err) => err !== null);
    if (hasErrors) {
      const errorField = document.querySelector('.is-invalid');
      if (errorField) {
        const group = errorField.closest('.form-groups');
        if (group) {
          group.scrollIntoView({ behavior: 'smooth', block: 'start' });
          const input = group.querySelector('input, textarea, select');
          input?.focus({ preventScroll: true });
        } else {
          errorField.scrollIntoView({ behavior: 'smooth', block: 'start' });
          errorField.focus({ preventScroll: true });
        }
      }
    }
    setSubmitted(false);
  }, [formError]);

  function handleSubmit(e) {
    e.preventDefault();
    setValidateDescriptionOnChange(true);
    setValidateEmailOnChange(true);
    setValidateBusinessOnChange(true);
    setValidateProductOnChange(true);
    setSubmitted(true);

    const errors = validateFormFields(true);
    if (!Object.values(errors).some((err) => err !== null)) {
      sendAPI();
      setScreenReaderAlert(t('ScreenReader_ContactForm_Success'));
    } else {
      setScreenReaderAlert(t('ScreenReader_ContactForm_Error'));
    }
  }

  useEffect(() => {
    if (widgetMode) {
      document.getElementById('desktop-contact-us').style.padding = '0px';
    }
  }, []);

  useEffect(() => {
    async function updateSearchResults() {
      const results = await performSearch(searchTerm, 'partners', lang);
      if (results.length > 0) {
        setSuggestedArticleAlertRead(true);
        setScreenReaderAlert(t('FAQ_ScreenReaderAlert_Suggested_Article', null, lang));
      } else if (suggestedArticleAlertRead) {
        setScreenReaderAlert(t('FAQ_ScreenReaderAlert_No_Suggested_Article', null, lang));
      }
      setSearchResults(results);
    }
    const timer = setTimeout(updateSearchResults, 700);
    return () => { clearTimeout(timer); };
  }, [searchTerm, lang]);

  useEffect(() => {
    validateFormFields();
  }, [
    lang,
    validateBusinessOnChange,
    validateEmailOnChange,
    validateDescriptionOnChange,
    validateProductOnChange,
  ]);

  // validates product on select interaction
  useEffect(() => {
    if (validateProductOnChange) {
      validateFormFields();
    }
  }, [product, validateProductOnChange]);

  return (
    <>
      {showError && (
        <Alert
          variant="danger"
          id="error_message"
          className="mt-3"
          onClose={() => {
            setShowError(false);
            setError('');
          }}
          dismissible
        >
          <p style={{ marginBottom: 0 }}>{error}</p>
        </Alert>
      )}
      <div id="successMessage">
        <h1>{t('ContactForm_Submitted')}</h1>
        <Link to={(widgetMode) ? `/widget/partners/${lang}${isMobile}` : `/partners/${lang}/categories`}>
          <Button className="error-go-home-btn partners-button">{t('ReturnHome_Btn')}</Button>
        </Link>
      </div>
      <div id="desktop-contact-us">
        <div id="skip-link-content">
          <h1>{t('General_Contact_Us')}</h1>
          <p className="contact-label-suggestion">{t('ContactForm_Fields_Required')}</p>
          <Form noValidate onSubmit={handleSubmit}>

            {/* Email */}
            <Form.Group
              className="form-groups"
              controlId="ContactForm_General_Email_"
              required
            >
              <Form.Label as="legend" id="aria-email" className="form-labels">
                {t('ContactForm_General_Email_Label')}
              </Form.Label>
              <Form.Text className="muted helpText">
                {t('ContactForm_General_Email_Help')}
              </Form.Text>
              <Form.Control
                aria-labelledby="aria-email aria-email-warn"
                ref={emailRef}
                className={formError.customerEmail ? 'is-invalid' : ''}
                onBlur={() => {
                  setValidateEmailOnChange(true);
                  validateFormFields();
                }}
                onChange={() => {
                  if (validateEmailOnChange) validateFormFields();
                  setRunMailCheck(true);
                }}
              />
              {formError.customerEmail && (
                <span className="errorMessage">{formError.customerEmail}</span>
              )}
              <div className="invalidEmailContainer">
                {showEmailConfirm && (
                  <div>
                    <Button
                      text={t('AccountDetails_MailCheck_ButtonYes')}
                      fill="outline"
                      className="mailCheckButton clickable-btn inactive-btn"
                      aria-label={t('Contact_EmailSuggestionYes').replace('xEmail', invalidEmailSuggestion)}
                      onClick={() => {
                        setRunMailCheck(false);
                        emailRef.current.value = invalidEmailSuggestion;
                        formError.customerEmail = null;
                        setInvalidEmailSuggestion(null);
                        setShowEmailConfirm(false);
                      }}
                    >
                      {t('Yes_Text')}
                    </Button>
                    <Button
                      text={t('AccountDetails_MailCheck_ButtonNo')}
                      fill="outline"
                      className="mailCheckButton clickable-btn inactive-btn"
                      aria-label={t('Contact_EmailSuggestionNo')}
                      onClick={() => {
                        setShowEmailConfirm(false);
                        setRunMailCheck(false);
                        formError.email = null;
                      }}
                    >
                      {t('No_Text')}
                    </Button>
                  </div>
                )}

              </div>
            </Form.Group>

            {/* Business Name */}
            <Form.Group className="form-groups" controlId="ContactForm_Partners_BusinessName_" required>
              <Form.Label as="legend" id="aria-desc" className="form-labels">
                {t('ContactForm_Business_Name')}
              </Form.Label>
              <Form.Control
                aria-labelledby="aria-desc"
                ref={businessRef}
                className={formError.businessName ? 'is-invalid' : ''}
                onBlur={() => setValidateBusinessOnChange(true)}
                onChange={() => {
                  if (validateBusinessOnChange) validateFormFields();
                }}
              />
              {formError.businessName && (
                <span className="errorMessage">{formError.businessName}</span>
              )}
            </Form.Group>

            {/* Dropdown: Product */}
            <Form.Group
              className="form-groups"
              controlId="ContactForm_Product_"
            >
              <fieldset>
                <Form.Label id="option_label" as="legend" className="form-labels">
                  {t('ContactForm_Product_Title')}
                </Form.Label>

                {/* Dropdown: Product */}
                <select
                  className={`custom-select ${formError.product ? 'is-invalid' : ''}`}
                  id="product"
                  onBlur={() => setValidateProductOnChange(true)}
                  onChange={(e) => setProduct(e.target.value)}
                >
                  <option value="" disabled selected>
                    {t('ContactForm_PartnerPay_SelectPlaceholder')}
                  </option>
                  <option value="PartnerPay / Interac e-Transfer">{t('ContactForm_PaymentOption_PartnerPay')}</option>
                  <option value="PaySimply">PaySimply</option>
                  <option value="Pay in person at Canada Post">{t('ContactForm_PaymentOption_InPerson')}</option>
                </select>
              </fieldset>
              {formError.product && (
                <span className="errorMessage">{formError.product}</span>
              )}
            </Form.Group>

            {/* How can we help */}
            <Form.Group
              className="form-groups"
              controlId="ContactForm_General_Body_"
              required
            >
              <fieldset>
                <Form.Label as="legend" id="aria-desc" className="form-labels">
                  {t('ContactForm_General_Body_Label')}
                </Form.Label>

                <Form.Control
                  aria-labelledby="aria-desc"
                  as="textarea"
                  rows={8}
                  ref={bodyRef}
                  className={formError.description ? 'is-invalid' : ''}
                  onBlur={() => setValidateDescriptionOnChange(true)}
                  onChange={(e) => {
                    handleTextareaChange(e);
                    if (validateDescriptionOnChange) validateFormFields();
                  }}
                />
                {formError.description && (
                  <span className="errorMessage">{formError.description}</span>
                )}
              </fieldset>
            </Form.Group>
            <RelevantFAQ searchResults={searchResults} brand="partners" lang={lang} />
            <Button
              id="submit-button"
              className="paysimply-button"
              type="submit"
              disabled={disable}
              size="lg"
              style={{ backgroundColor: '#2B4E7B' }}
            >
              {t('ContactForm_Submit_button')}
            </Button>

          </Form>
          <Alert className="mt-3 email-banner" show={process.env.REACT_APP_ENV === 'dev'}>
            On the test system, contact form emails will be sent to
            <strong> psidevstar@gmail.com</strong>
          </Alert>
        </div>
      </div>
    </>
  );
}

export default translate(ContactUsPartners);
